import { defineStore } from "pinia";
import api from "@/api";
import WebSocketService from "@/services/webSocket";
import { useUserStore } from "@/stores/user";
import { useOrganizationStore } from "@/stores/organization";
import { MessageResponse } from "@/schemas/messenger";
import { pause } from "@/api/utils";
import { generateSafeFilename } from "@/utils/utils";

export const useMessengerStore = defineStore("messenger", {
  state: () => ({
    accessToken: "",
    refreshToken: "",
    orgToken: "",
    connectionId: "",
    messageObjs: [],
    websocket: null,
    isConnected: false,
    isConnectionLoading: false,
    // for auto reconnection
    isManualDisconnection: false,
    reconnectionAttempts: 0,
  }),
  getters: {
    lastMessageObj() {
      const lastMessageObj = this.messageObjs[this.messageObjs.length - 1];
      return lastMessageObj;
    },
    isBotLoading() {
      return (
        this.messageObjs.length !== 0 && this.lastMessageObj?.msg_type !== "bot"
      );
    },
  },
  actions: {
    async fetchAuthTokens() {
      const userStore = useUserStore();
      // console.log("fetchAuthTokens invoked!");
      try {
        const { data } = await api.messenger.getAuthTokensForAcServer(
          userStore.activeOrganization
        );
        this.accessToken = data.access_token;
        this.refreshToken = data.refresh_token;
        this.orgToken = data.org_token;
      } catch (err) {
        console.error(err.message);
      }
    },

    async sendMessage(message, requestId = null, attachments = []) {
      const userStore = useUserStore();
      const userEmail = userStore?.authUserEmail;

      // This is what is rendered on the frontend
      this.messageObjs.push(
        new MessageResponse(
          userEmail,
          "user",
          message,
          true,
          attachments.map((fileObj) => ({
            filename: fileObj.name,
            url: URL.createObjectURL(fileObj),
          }))
        )
      );

      const { transformedAttachments } =
        await this.handleAttachmentsUpload(attachments);

      // This is what is sent to the websocket
      const messageRequestObj = {
        version: "v1",
        userid: userEmail,
        message,
        request_id: requestId,
        attachments: transformedAttachments,
      };

      this.websocket.send(JSON.stringify(messageRequestObj));
    },

    receiveMessage(data) {
      const JSONData = JSON.parse(data);
      // Create a uniform messageObj schema, no types...
      // console.log("responseMessage: ", JSONData);
      this.messageObjs.push(...JSONData);
    },

    async handleAttachmentsUpload(attachments) {
      // initalize stores
      const userStore = useUserStore();
      const organizationStore = useOrganizationStore();

      // results
      const transformedAttachments = [];
      let presignedFilesData = {};

      if (!attachments.length)
        return { transformedAttachments, presignedFilesData };

      // console.log("attachments: ", toRaw(attachments));

      const data = {
        files: attachments.map((file) => ({
          object_key: generateSafeFilename(file.name),
          content_type: file.type || "application/octet-stream",
        })),
        user_id: userStore.authUserId,
        organization_id: organizationStore.organizationId,
      };

      try {
        const { data: postPresignedUrls } =
          await api.messenger.createPostPresignedUrls(data);
        console.log("presignedUrls: ", postPresignedUrls);

        if (!postPresignedUrls.length) {
          throw new Error("no presigned urls!");
        }

        // loop through postPresignedUrls
        for (let i = 0; i < postPresignedUrls.length; i++) {
          // upload image
          await this.uploadFileByS3PresignedURL(
            attachments[i],
            postPresignedUrls[i]["post_presigned_url"]
          );

          // create message object
          transformedAttachments.push({
            file_name: postPresignedUrls[i].file_name,
            s3_key: postPresignedUrls[i].key,
            bucket: postPresignedUrls[i].bucket,
          });
        }
      } catch (err) {
        console.error("Error during attachment handling:", err);
      }

      return {
        transformedAttachments,
        presignedFilesData,
      };
    },

    async fetchFileByS3PresignedURL(attachments = []) {
      const presignedUrl = `${import.meta.env.VITE_APP_AC_SERVER_HTTPS_URL}/api/attachments/create/get-presigned-url?token=${this.accessToken}`;

      const presignedAttachments = attachments.map(({ name, type }) => ({
        content_type: type,
        name,
      }));

      const presignedFilesDataRes = await fetch(presignedUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ attachments: presignedAttachments }),
      });

      return { presignedFilesDataRes };
    },

    async uploadFileByS3PresignedURL(file, presigned_url) {
      const formData = new FormData();

      // Append fields from the response
      Object.entries(presigned_url.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Append the file to the form data
      formData.append("file", file, file.name);

      try {
        const httpResponse = await fetch(presigned_url.url, {
          method: "POST",
          body: formData,
        });
        console.log("httpResponse: ", httpResponse);
        return httpResponse.status === 204;
      } catch (error) {
        return false;
      }
    },

    initializeWebSocketConnection() {
      // reset this state value
      this.isManualDisconnection = false;
      const url = `${import.meta.env.VITE_APP_AC_SERVER_WS_URL}/api/livechat/in/${this.orgToken}?token=${this.accessToken}&channel=web`;
      const websocket = new WebSocketService(url);
      this.websocket = websocket;

      // add events and listeners before connecting to websocket
      websocket.on("connected", (val) => {
        console.log("event connected: ", val);
        this.isConnected = val;
        this.isConnectionLoading = false;
        this.reconnectionAttempts = 0;
      });
      websocket.on("disconnected", async (val) => {
        console.log("event disconnected: ", val);
        this.isConnected = val;
        this.websocket = null;

        const maxReconnectionAttempts = 3;
        // Attempt to reconnect if disconnection wasnt manual
        if (
          !this.isManualDisconnection &&
          this.reconnectionAttempts < maxReconnectionAttempts
        ) {
          this.reconnectionAttempts++;
          console.log(`Reconnecting... Attempt ${this.reconnectionAttempts}`);
          // TO DO: future proof,
          // When rtcToAcServer runs,
          // if it fails, the disconnected handler runs before it proceeds to the next line
          await this.rtcToAcServer();
          // exit here
          return;
        }

        this.isConnectionLoading = false;
      });

      // add receive listener
      websocket.addListener((message) => this.receiveMessage(message));

      // connect
      websocket.connect();
    },

    // Manually Close Connection
    closeConnection() {
      this.isManualDisconnection = true;
      if (this.websocket) {
        // call close method on websocket service
        this.websocket.close();
      }
    },

    // websocket connection to ac server
    async rtcToAcServer() {
      this.isConnectionLoading = true;
      await pause(300);
      if (this.websocket === null) {
        try {
          // Fetch auth tokens
          await this.fetchAuthTokens();
        } catch (err) {
          console.err("Error fetching Auth Tokens: ", err.message);
          throw err;
        }

        try {
          // Init websocket connection to ac server
          this.initializeWebSocketConnection();
        } catch (err) {
          console.err("error in rtcToAcServer method: ", err.message);
        }
      }
    },
  },
});
